import { useState, useEffect } from 'react'
import { observer } from 'mobx-react-lite'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Badge from '@/components/common/Badge'
import Modal from '@/components/common/Modal'
import Input from '@/components/common/Input'
import { mcpStore } from '@/stores/MCPStore'
import type { MCPServer, MCPServerCreate, MCPTool, MCPToolCall } from '@/types'
import {
  ServerIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  PlayIcon,
  WrenchIcon,
  SignalIcon,
  ChevronRightIcon,
  XMarkIcon,
  CommandLineIcon,
} from '@heroicons/react/24/outline'

// Server type options
const SERVER_TYPES = [
  { value: 'http', label: 'HTTP', description: 'REST API based MCP server' },
  { value: 'websocket', label: 'WebSocket', description: 'WebSocket based real-time server' },
  { value: 'stdio', label: 'Stdio', description: 'Standard I/O based local server' },
  { value: 'sse', label: 'SSE', description: 'Server-Sent Events based server' },
]

// Example MCP servers for quick setup
const EXAMPLE_SERVERS = [
  { id: 'filesystem', name: 'File System', type: 'http', url: 'http://localhost:3100', description: 'Read and write files' },
  { id: 'web-search', name: 'Web Search', type: 'http', url: 'http://localhost:3101', description: 'Search the web' },
  { id: 'github', name: 'GitHub', type: 'http', url: 'http://localhost:3102', description: 'GitHub API integration' },
  { id: 'database', name: 'Database', type: 'http', url: 'http://localhost:3103', description: 'Execute SQL queries' },
]

function MCPServersPage() {
  const [showRegisterModal, setShowRegisterModal] = useState(false)
  const [showToolModal, setShowToolModal] = useState(false)
  const [selectedServerForTool, setSelectedServerForTool] = useState<MCPServer | null>(null)
  const [selectedTool, setSelectedTool] = useState<MCPTool | null>(null)
  const [toolArguments, setToolArguments] = useState<string>('{}')
  const [expandedServer, setExpandedServer] = useState<string | null>(null)

  // Form state for registration
  const [formData, setFormData] = useState<MCPServerCreate>({
    id: '',
    name: '',
    type: 'http',
    url: '',
    api_key: '',
    metadata: {},
  })

  useEffect(() => {
    mcpStore.fetchServers()
  }, [])

  const handleRegister = async () => {
    try {
      await mcpStore.registerServer(formData)
      setShowRegisterModal(false)
      setFormData({ id: '', name: '', type: 'http', url: '', api_key: '', metadata: {} })
    } catch (error) {
      // Error is handled in store
    }
  }

  const handleUnregister = async (serverId: string) => {
    if (confirm('Are you sure you want to unregister this MCP server?')) {
      await mcpStore.unregisterServer(serverId)
    }
  }

  const handleQuickAdd = (example: typeof EXAMPLE_SERVERS[0]) => {
    setFormData({
      id: example.id,
      name: example.name,
      type: example.type,
      url: example.url,
      api_key: '',
      metadata: { description: example.description },
    })
    setShowRegisterModal(true)
  }

  const handleToolClick = async (server: MCPServer, tool: MCPTool) => {
    setSelectedServerForTool(server)
    setSelectedTool(tool)
    setToolArguments('{}')
    setShowToolModal(true)
  }

  const handleExecuteTool = async () => {
    if (!selectedServerForTool || !selectedTool) return

    try {
      const args = JSON.parse(toolArguments)
      const toolCall: MCPToolCall = {
        server_id: selectedServerForTool.id,
        tool_name: selectedTool.name,
        arguments: args,
        timeout: 30,
      }
      await mcpStore.callTool(toolCall)
      setShowToolModal(false)
    } catch (error) {
      if (error instanceof SyntaxError) {
        alert('Invalid JSON in arguments')
      }
    }
  }

  const handleExpandServer = async (serverId: string) => {
    if (expandedServer === serverId) {
      setExpandedServer(null)
    } else {
      setExpandedServer(serverId)
      await mcpStore.fetchServer(serverId)
    }
  }

  const getHealthStatus = (serverId: string) => {
    const health = mcpStore.serverHealth[serverId]
    if (!health) return 'unknown'
    return health.healthy ? 'healthy' : 'unhealthy'
  }

  const getHealthBadge = (serverId: string) => {
    const status = getHealthStatus(serverId)
    switch (status) {
      case 'healthy':
        return <Badge variant="success">Healthy</Badge>
      case 'unhealthy':
        return <Badge variant="error">Unhealthy</Badge>
      default:
        return <Badge variant="gray">Unknown</Badge>
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">MCP Servers</h1>
          <p className="text-muted-foreground mt-1">
            Model Context Protocol integration - Connect external tools and services
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            onClick={() => mcpStore.fetchServers()}
            disabled={mcpStore.isLoading}
          >
            <ArrowPathIcon className={`w-4 h-4 mr-2 ${mcpStore.isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="primary" onClick={() => setShowRegisterModal(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Register Server
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {mcpStore.error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-600">{mcpStore.error}</span>
          <button onClick={() => mcpStore.clearError()} className="text-red-600 hover:text-red-600">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Quick Add Section */}
      {mcpStore.servers.length === 0 && !mcpStore.isLoading && (
        <Card>
          <h3 className="text-lg font-semibold text-foreground mb-4">Quick Setup</h3>
          <p className="text-muted-foreground mb-4">
            Get started quickly by adding common MCP servers:
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {EXAMPLE_SERVERS.map((example) => (
              <div
                key={example.id}
                className="border border-border rounded-lg p-4 hover:border-blue-500 hover:bg-blue-500/10 cursor-pointer transition-colors"
                onClick={() => handleQuickAdd(example)}
              >
                <div className="flex items-center gap-2 mb-2">
                  <WrenchIcon className="w-5 h-5 text-muted-foreground" />
                  <span className="font-medium text-foreground">{example.name}</span>
                </div>
                <p className="text-sm text-muted-foreground">{example.description}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Server List */}
      {mcpStore.servers.length > 0 && (
        <div className="space-y-4">
          {mcpStore.servers.map((server) => (
            <Card key={server.id} className="overflow-hidden">
              <div
                className="flex items-center justify-between cursor-pointer"
                onClick={() => handleExpandServer(server.id)}
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-500/15 rounded-lg">
                    <ServerIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-foreground">{server.name}</h3>
                      <Badge variant="gray">{server.type.toUpperCase()}</Badge>
                      {getHealthBadge(server.id)}
                    </div>
                    <p className="text-sm text-muted-foreground">{server.url}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground">
                    {server.tools_count} tool{server.tools_count !== 1 ? 's' : ''}
                  </span>
                  <ChevronRightIcon
                    className={`w-5 h-5 text-muted-foreground transition-transform ${
                      expandedServer === server.id ? 'rotate-90' : ''
                    }`}
                  />
                </div>
              </div>

              {/* Expanded Content */}
              {expandedServer === server.id && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-foreground">Available Tools</h4>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          mcpStore.refreshServerTools(server.id)
                        }}
                      >
                        <ArrowPathIcon className="w-4 h-4 mr-1" />
                        Refresh Tools
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          mcpStore.checkServerHealth(server.id)
                        }}
                      >
                        <SignalIcon className="w-4 h-4 mr-1" />
                        Check Health
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleUnregister(server.id)
                        }}
                      >
                        <TrashIcon className="w-4 h-4 mr-1" />
                        Unregister
                      </Button>
                    </div>
                  </div>

                  {mcpStore.selectedServer?.id === server.id && mcpStore.selectedServer.tools ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {mcpStore.selectedServer.tools.map((tool) => (
                        <div
                          key={tool.name}
                          className="border border-border rounded-lg p-3 hover:border-blue-500 hover:bg-blue-500/10 cursor-pointer transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleToolClick(server, tool)
                          }}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <CommandLineIcon className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium text-foreground">{tool.name}</span>
                          </div>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {tool.description || 'No description available'}
                          </p>
                          {tool.parameters && Object.keys(tool.parameters).length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                              {Object.keys(tool.parameters).slice(0, 3).map((param) => (
                                <Badge key={param} variant="gray">
                                  {param}
                                </Badge>
                              ))}
                              {Object.keys(tool.parameters).length > 3 && (
                                <Badge variant="gray">+{Object.keys(tool.parameters).length - 3}</Badge>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      {mcpStore.isLoading ? (
                        <div className="flex items-center justify-center gap-2">
                          <ArrowPathIcon className="w-5 h-5 animate-spin" />
                          Loading tools...
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2">
                          <WrenchIcon className="w-8 h-8" />
                          <p>No tools discovered yet</p>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation()
                              mcpStore.refreshServerTools(server.id)
                            }}
                          >
                            Discover Tools
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* Empty State */}
      {mcpStore.servers.length === 0 && !mcpStore.isLoading && (
        <Card className="text-center py-12">
          <ServerIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">No MCP Servers Registered</h3>
          <p className="text-muted-foreground mb-4">
            Register MCP servers to enable tool usage for your agents
          </p>
          <Button variant="primary" onClick={() => setShowRegisterModal(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Register Your First Server
          </Button>
        </Card>
      )}

      {/* Tool Execution Results */}
      {mcpStore.toolCallResults.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-foreground">Recent Tool Executions</h3>
            <Button variant="secondary" size="sm" onClick={() => mcpStore.clearToolResults()}>
              Clear Results
            </Button>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {mcpStore.toolCallResults.map((result, index) => (
              <div
                key={index}
                className={`p-3 rounded-lg border ${
                  result.success
                    ? 'bg-green-500/10 border-green-500/20'
                    : 'bg-red-500/10 border-red-500/20'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant={result.success ? 'success' : 'error'}>
                      {result.success ? 'Success' : 'Failed'}
                    </Badge>
                    <span className="font-medium text-foreground">
                      {result.tool_name}
                    </span>
                    <span className="text-muted-foreground">on {result.server_id}</span>
                  </div>
                </div>
                <pre className="text-sm bg-card p-2 rounded border overflow-x-auto">
                  {result.error
                    ? result.error
                    : JSON.stringify(result.result, null, 2)}
                </pre>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Register Server Modal */}
      <Modal
        isOpen={showRegisterModal}
        onClose={() => setShowRegisterModal(false)}
        title="Register MCP Server"
      >
        <div className="space-y-4">
          <Input
            label="Server ID"
            value={formData.id}
            onChange={(e) => setFormData({ ...formData, id: e.target.value })}
            placeholder="e.g., my-mcp-server"
            required
          />
          <Input
            label="Server Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="e.g., My MCP Server"
            required
          />
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Server Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-blue-500"
            >
              {SERVER_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label} - {type.description}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Server URL"
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            placeholder="e.g., http://localhost:3000"
            required
          />
          <Input
            label="API Key (optional)"
            type="password"
            value={formData.api_key || ''}
            onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
            placeholder="Enter API key if required"
          />

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowRegisterModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRegister}
              disabled={!formData.id || !formData.name || !formData.url || mcpStore.isRegistering}
            >
              {mcpStore.isRegistering ? 'Registering...' : 'Register Server'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Tool Execution Modal */}
      <Modal
        isOpen={showToolModal}
        onClose={() => setShowToolModal(false)}
        title={`Execute Tool: ${selectedTool?.name || ''}`}
      >
        <div className="space-y-4">
          {selectedTool && (
            <>
              <div>
                <p className="text-muted-foreground mb-4">{selectedTool.description}</p>
                {selectedTool.parameters && Object.keys(selectedTool.parameters).length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-foreground mb-2">Parameters:</h4>
                    <div className="bg-muted/50 p-3 rounded-lg">
                      {Object.entries(selectedTool.parameters).map(([name, type]) => (
                        <div key={name} className="flex items-center gap-2 text-sm">
                          <span className="font-mono text-blue-600">{name}</span>
                          <span className="text-muted-foreground">: {String(type)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Arguments (JSON)
                </label>
                <textarea
                  value={toolArguments}
                  onChange={(e) => setToolArguments(e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-blue-500 font-mono text-sm"
                  rows={6}
                  placeholder='{"key": "value"}'
                />
              </div>
            </>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setShowToolModal(false)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExecuteTool}
              disabled={mcpStore.isCallingTool}
            >
              {mcpStore.isCallingTool ? (
                <>
                  <ArrowPathIcon className="w-4 h-4 mr-2 animate-spin" />
                  Executing...
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4 mr-2" />
                  Execute
                </>
              )}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default observer(MCPServersPage)
